import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    num: "01",
    title: "Custom Kitchens",
    desc: "From shaker classics to handleless contemporary — every cabinet is made to measure and CNC-machined to the millimetre in our workshop, then expertly fitted.",
    points: ["In-frame & handleless styles", "Stone, timber & brass worktops", "Integrated appliances & lighting"],
  },
  {
    num: "02",
    title: "Wardrobes & Dressing Rooms",
    desc: "Walk-in wardrobes and fitted bedroom storage designed around how you live — with valet drawers, jewellery trays and illuminated display shelving.",
    points: ["Walk-in & fitted wardrobes", "Glass-fronted display cabinets", "Bespoke interior fittings"],
  },
  {
    num: "03",
    title: "Living Space Cabinetry",
    desc: "Media walls, library shelving, alcove units and home bars that turn dead space into the room's centrepiece.",
    points: ["Media walls & TV cabinetry", "Library & alcove shelving", "Home bars & display units"],
  },
  {
    num: "04",
    title: "Flatpack & DIY Options",
    desc: "Whether you're a tradie or an enthusiastic DIYer, we're here to help with your project. Bring your ideas — we'll precision-cut and label every panel for flatpack assembly, or supply fully assembled cabinets ready for you to install.",
    points: ["Labelled flatpack panels", "Or fully assembled cabinets", "Cut to your measurements"],
  },
  {
    num: "05",
    title: "Bathroom Vanities",
    desc: "Floating and floor-standing vanities machined to your bathroom's exact dimensions — moisture-resistant finishes, soft-close drawers and stone-ready tops.",
    points: ["Floating & floor-standing", "Moisture-resistant finishes", "Custom sizes & storage"],
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 lg:py-32 bg-espresso-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <p className="section-label" data-testid="services-label">What We Make</p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-cream mt-4" data-testid="services-heading">
          One workshop. Six ways to work with us. <span className="italic text-brass">No compromises.</span>
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.num}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            data-testid={`service-card-${i}`}
            className="group border border-espresso-600 bg-espresso-800 p-10 hover:border-brass/50 transition-colors duration-300"
          >
            <div className="flex items-start justify-between">
              <span className="font-serif text-5xl text-espresso-600 group-hover:text-brass/40 transition-colors">{s.num}</span>
              <ArrowUpRight size={20} className="text-espresso-600 group-hover:text-brass transition-colors" />
            </div>
            <h3 className="font-serif text-xl sm:text-2xl text-cream mt-8">{s.title}</h3>
            <p className="text-sm text-cream-muted leading-relaxed mt-4">{s.desc}</p>
            <ul className="mt-6 space-y-2">
              {s.points.map((p) => (
                <li key={p} className="text-xs text-cream-muted tracking-wide flex items-center gap-2">
                  <span className="w-3 h-px bg-brass inline-block" /> {p}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        data-testid="service-card-cnc"
        className="group grid grid-cols-1 md:grid-cols-5 gap-0 border border-espresso-600 bg-espresso-800 mt-8 hover:border-brass/50 transition-colors duration-300 overflow-hidden"
      >
        <div className="md:col-span-2 overflow-hidden max-h-[420px] md:max-h-none">
          <img
            src="/cnc-craft.jpg"
            alt="The Golden Mile Interiors CNC machine in the Delacombe workshop"
            loading="lazy"
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
          />
        </div>
        <div className="md:col-span-3 p-10 lg:p-14">
          <div className="flex items-start justify-between">
            <span className="font-serif text-5xl text-espresso-600 group-hover:text-brass/40 transition-colors">06</span>
            <ArrowUpRight size={20} className="text-espresso-600 group-hover:text-brass transition-colors" />
          </div>
          <h3 className="font-serif text-xl sm:text-2xl text-cream mt-8">Custom CNC & Product Prototyping</h3>
          <p className="text-sm text-cream-muted leading-relaxed mt-4 max-w-xl">
            Have a design, a one-off part, or a product idea? Our CNC machines cut, carve
            and engrave timber, panels and more — from decorative doors and signage to
            full product prototypes. Bring a sketch, a photo or a CAD file and we'll
            machine it.
          </p>
          <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-2">
            {["One-off & small-batch CNC jobs", "Product prototyping", "Decorative routing & engraving"].map((p) => (
              <li key={p} className="text-xs text-cream-muted tracking-wide flex items-center gap-2">
                <span className="w-3 h-px bg-brass inline-block" /> {p}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </div>
  </section>
);
